<template>
  <AppLayout>
    <div class="flex flex-col min-h-full bg-slate-50 dark:bg-slate-950">

      <!-- Sticky top bar -->
      <div class="sticky top-0 z-20 bg-slate-50 dark:bg-slate-950 px-4 pt-4 pb-0">
        <div class="flex items-center justify-between mb-3">
          <div>
            <h1 class="text-xl font-bold text-slate-800 dark:text-white leading-tight">Invoices</h1>
            <p class="text-xs text-slate-400 mt-0.5">{{ periodLabel }}</p>
          </div>
          <button @click="showFilters = !showFilters"
            class="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition"
            :class="showFilters
              ? 'bg-indigo-500 text-white'
              : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"/>
            </svg>
            Filters
          </button>
        </div>

        <div v-if="showFilters"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 mb-3">
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Period</label>
              <select v-model="filters.preset"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Group By</label>
              <select v-model="filters.groupBy"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <template v-if="filters.preset === 'custom'">
              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">From</label>
                <input type="date" v-model="filters.from"
                  class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">To</label>
                <input type="date" v-model="filters.to"
                  class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="flex-1 px-4 pb-6 pt-3 space-y-4">

        <!-- KPI grid -->
        <div class="grid grid-cols-3 lg:grid-cols-6 gap-3">
          <div v-for="kpi in invoiceKPIs" :key="kpi.label"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 lg:p-4">
            <p class="text-[9px] lg:text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{{ kpi.label }}</p>
            <p class="text-sm lg:text-base font-bold leading-tight" :class="kpi.color">{{ kpi.value }}</p>
            <p v-if="kpi.sub" class="text-[9px] lg:text-[10px] text-slate-400 mt-1">{{ kpi.sub }}</p>
          </div>
        </div>

        <!-- Row: Value bar chart + Sale vs Purchase doughnut -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Sale vs Purchase value bar -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Invoice Value by Month</p>
            <Bar :data="invoiceChartData" :options="barOptions" style="max-height:200px" />
          </div>

          <!-- Sale vs Purchase count doughnut -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Sale vs Purchase Split</p>
            <div class="flex items-center gap-6">
              <div class="shrink-0">
                <Doughnut :data="splitDoughnutData" :options="doughnutOptions" style="width:130px;height:130px" />
              </div>
              <div class="flex-1 space-y-3">
                <div>
                  <div class="flex items-center justify-between mb-1">
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"></span>
                      <span class="text-xs text-slate-500 dark:text-slate-400">Sale</span>
                    </div>
                    <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400">32 · Rs 4.85L</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-emerald-500 rounded-full" style="width:67%"></div>
                  </div>
                </div>
                <div>
                  <div class="flex items-center justify-between mb-1">
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full bg-indigo-500 shrink-0"></span>
                      <span class="text-xs text-slate-500 dark:text-slate-400">Purchase</span>
                    </div>
                    <span class="text-xs font-bold text-indigo-600 dark:text-indigo-400">16 · Rs 3.12L</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-indigo-500 rounded-full" style="width:33%"></div>
                  </div>
                </div>
                <div class="border-t border-slate-100 dark:border-slate-800 pt-2 flex justify-between text-[10px] text-slate-400">
                  <span>Avg sale: <span class="font-semibold text-slate-700 dark:text-slate-300">Rs 15.2k</span></span>
                  <span>Avg pur: <span class="font-semibold text-slate-700 dark:text-slate-300">Rs 19.5k</span></span>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Row: Invoice count trend + Busiest months -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Invoice count line chart -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Invoice Count Trend</p>
            <Line :data="countTrendData" :options="lineOptions" style="max-height:180px" />
          </div>

          <!-- Busiest months highlight -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Monthly Highlights</p>
            <div class="space-y-2">
              <div v-for="h in monthHighlights" :key="h.label"
                class="flex items-center justify-between rounded-xl px-3 py-2.5"
                :class="h.bg">
                <div>
                  <p class="text-[10px] font-semibold uppercase tracking-wide" :class="h.labelColor">{{ h.label }}</p>
                  <p class="text-sm font-bold mt-0.5" :class="h.valueColor">{{ h.month }}</p>
                </div>
                <div class="text-right">
                  <p class="text-xs font-bold" :class="h.valueColor">{{ h.value }}</p>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ h.sub }}</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Row: Top customers by value + Top customers by count -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Top 5 customers by invoice value -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Top 5 by Value</p>
              <span class="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full">sale invoices</span>
            </div>
            <div class="space-y-3">
              <div v-for="c in topByValue" :key="c.name" class="flex items-center gap-3">
                <div class="w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center text-[10px] font-bold text-emerald-600 dark:text-emerald-400 shrink-0">
                  {{ c.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between mb-1">
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                    <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 ml-2 shrink-0">{{ shortNum(c.value) }}</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-emerald-500 rounded-full" :style="{ width: c.pct + '%' }"></div>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ c.count }} invoices · avg {{ shortNum(c.avg) }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Top 5 customers by invoice count -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Top 5 by Count</p>
              <span class="text-[10px] font-semibold bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full">most frequent</span>
            </div>
            <div class="space-y-3">
              <div v-for="c in topByCount" :key="c.name" class="flex items-center gap-3">
                <div class="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-[10px] font-bold text-indigo-600 dark:text-indigo-400 shrink-0">
                  {{ c.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between mb-1">
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ c.name }}</span>
                    <span class="text-xs font-bold text-indigo-600 dark:text-indigo-400 ml-2 shrink-0">{{ c.count }} invoices</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-indigo-500 rounded-full" :style="{ width: c.pct + '%' }"></div>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ shortNum(c.value) }} total · avg {{ shortNum(c.avg) }}</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Largest invoices -->
        <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
          <div class="flex items-center justify-between mb-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white">Largest Invoices</p>
            <span class="text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full">top 6 by value</span>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div v-for="inv in largestInvoices" :key="inv.number"
              class="flex items-center gap-3 rounded-xl p-3 border"
              :class="inv.type === 'sale'
                ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900'
                : 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-100 dark:border-indigo-900'">
              <div class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 font-bold text-xs"
                :class="inv.type === 'sale' ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400' : 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400'">
                #{{ inv.number }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold text-slate-800 dark:text-white truncate">{{ inv.customer }}</p>
                <p class="text-[10px] text-slate-400 mt-0.5">{{ inv.date }} · {{ inv.items }} items</p>
              </div>
              <div class="text-right shrink-0">
                <p class="text-xs font-bold" :class="inv.type === 'sale' ? 'text-emerald-600 dark:text-emerald-400' : 'text-indigo-600 dark:text-indigo-400'">
                  {{ shortNum(inv.value) }}
                </p>
                <span class="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full"
                  :class="inv.type === 'sale' ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400' : 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400'">
                  {{ inv.type }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Monthly breakdown -->
        <div>
          <!-- Filter pills -->
          <div class="flex gap-2 mb-3">
            <button @click="typeFilter = 'all'" class="text-xs font-semibold px-4 py-2 rounded-full transition"
              :class="typeFilter === 'all' ? 'bg-indigo-500 text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              All
            </button>
            <button @click="typeFilter = 'sale'" class="text-xs font-semibold px-4 py-2 rounded-full transition"
              :class="typeFilter === 'sale' ? 'bg-emerald-500 text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              Sale
            </button>
            <button @click="typeFilter = 'purchase'" class="text-xs font-semibold px-4 py-2 rounded-full transition"
              :class="typeFilter === 'purchase' ? 'bg-indigo-500 text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              Purchase
            </button>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div v-for="row in invoiceTableData" :key="row.period"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <div class="flex items-center justify-between mb-3">
                <div>
                  <p class="font-semibold text-slate-800 dark:text-white text-sm">{{ row.period }}</p>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ row.saleCount + row.purchaseCount }} total invoices</p>
                </div>
                <div class="text-right">
                  <p class="text-sm font-bold text-slate-800 dark:text-white">{{ shortNum(row.saleValue + row.purchaseValue) }}</p>
                  <p class="text-[10px] font-semibold mt-0.5"
                    :class="row.mom > 0 ? 'text-emerald-500' : row.mom < 0 ? 'text-red-400' : 'text-slate-400'">
                    {{ row.mom > 0 ? '↑' : row.mom < 0 ? '↓' : '—' }} {{ Math.abs(row.mom) }}% MoM
                  </p>
                </div>
              </div>
              <div class="grid gap-2" :class="typeFilter === 'all' ? 'grid-cols-2' : 'grid-cols-1'">
                <div v-if="typeFilter !== 'purchase'" class="bg-emerald-50 dark:bg-emerald-950 rounded-xl p-3">
                  <p class="text-[10px] text-slate-400 mb-1">Sale Invoices</p>
                  <p class="text-sm font-bold text-emerald-600 dark:text-emerald-400">{{ shortNum(row.saleValue) }}</p>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ row.saleCount }} invoices · avg {{ shortNum(row.saleValue / row.saleCount) }}</p>
                </div>
                <div v-if="typeFilter !== 'sale'" class="bg-indigo-50 dark:bg-indigo-950 rounded-xl p-3">
                  <p class="text-[10px] text-slate-400 mb-1">Purchase Invoices</p>
                  <p class="text-sm font-bold text-indigo-600 dark:text-indigo-400">{{ shortNum(row.purchaseValue) }}</p>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ row.purchaseCount }} invoices · avg {{ shortNum(row.purchaseValue / row.purchaseCount) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Bar, Line, Doughnut } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js'
import AppLayout from '@/layouts/AppLayout.vue'

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler)

const page = usePage()
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')
onMounted(() => { document.title = `Invoices Report | ${businessName.value}` })

const showFilters = ref(false)
const typeFilter  = ref('all')
const filters = ref({ preset: 'this_year', groupBy: 'monthly', from: '', to: '' })

const periodLabel = computed(() => {
  const map = { this_month: 'This Month', last_month: 'Last Month', this_year: 'This Year', last_year: 'Last Year', custom: 'Custom Range' }
  return map[filters.value.preset] || 'Custom Range'
})

function shortNum(n) {
  n = Number(n) || 0
  if (n >= 100000) return 'Rs ' + (n / 100000).toFixed(1) + 'L'
  if (n >= 1000)   return 'Rs ' + (n / 1000).toFixed(1) + 'k'
  return 'Rs ' + Math.round(n)
}

const MONTHS    = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const tickColor = '#94a3b8'
const gridColor = 'rgba(148,163,184,0.12)'
const baseFont  = { family: 'inherit', size: 10 }

// ── KPIs ─────────────────────────────────────────────
const invoiceKPIs = [
  { label: 'Total Invoices', value: '48',       color: 'text-slate-800 dark:text-white',          sub: 'this period' },
  { label: 'Sale Invoices',  value: '32',        color: 'text-emerald-600 dark:text-emerald-400',  sub: 'Rs 4.85L' },
  { label: 'Pur. Invoices',  value: '16',        color: 'text-indigo-600 dark:text-indigo-400',    sub: 'Rs 3.12L' },
  { label: 'Avg Sale Val',   value: 'Rs 15.2k',  color: 'text-slate-800 dark:text-white',          sub: 'per invoice' },
  { label: 'Avg Pur. Val',   value: 'Rs 19.5k',  color: 'text-slate-800 dark:text-white',          sub: 'per invoice' },
  { label: 'Busiest Month',  value: 'Oct',        color: 'text-indigo-600 dark:text-indigo-400',    sub: '9 invoices' },
]

// ── Value bar chart ───────────────────────────────────
const barOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { labels: { color: tickColor, font: baseFont, boxWidth: 10, padding: 12 } }, tooltip: { mode: 'index' } },
  scales: {
    x: { grid: { display: false }, ticks: { color: tickColor, font: baseFont } },
    y: { grid: { color: gridColor }, ticks: { color: tickColor, font: baseFont, callback: v => (v / 1000) + 'k' } },
  },
}
const invoiceChartData = {
  labels: MONTHS,
  datasets: [
    { label: 'Sale',     data: [42000,38000,55000,61000,48000,72000,65000,58000,80000,74000,68000,85000], backgroundColor: 'rgba(16,185,129,0.85)', borderRadius: 3 },
    { label: 'Purchase', data: [28000,25000,36000,40000,31000,48000,42000,38000,52000,49000,44000,56000], backgroundColor: 'rgba(99,102,241,0.85)', borderRadius: 3 },
  ],
}

// ── Split doughnut ────────────────────────────────────
const doughnutOptions = { responsive: false, plugins: { legend: { display: false } }, cutout: '70%' }
const splitDoughnutData = {
  labels: ['Sale', 'Purchase'],
  datasets: [{ data: [32, 16], backgroundColor: ['rgba(16,185,129,0.85)', 'rgba(99,102,241,0.85)'], borderWidth: 0 }],
}

// ── Count trend line ──────────────────────────────────
const lineOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { labels: { color: tickColor, font: baseFont, boxWidth: 10, padding: 12 } }, tooltip: { mode: 'index' } },
  scales: {
    x: { grid: { display: false }, ticks: { color: tickColor, font: baseFont } },
    y: { grid: { color: gridColor }, ticks: { color: tickColor, font: baseFont, stepSize: 1 } },
  },
  elements: { line: { tension: 0.4 }, point: { radius: 2, hoverRadius: 5 } },
}
const countTrendData = {
  labels: MONTHS,
  datasets: [
    { label: 'Sale',     data: [3,2,4,4,3,5,4,4,6,5,4,5], borderColor: 'rgba(16,185,129,1)',  backgroundColor: 'rgba(16,185,129,0.06)', fill: false },
    { label: 'Purchase', data: [1,1,2,2,2,3,2,2,3,2,2,3], borderColor: 'rgba(99,102,241,1)',  backgroundColor: 'rgba(99,102,241,0.06)', fill: false },
  ],
}

// ── Monthly highlights ────────────────────────────────
const monthHighlights = [
  { label: 'Busiest Month',   month: 'October 2025', value: '9 invoices',   sub: 'Rs 1.23L total',  bg: 'bg-indigo-50 dark:bg-indigo-950/40',  labelColor: 'text-indigo-500', valueColor: 'text-indigo-700 dark:text-indigo-300' },
  { label: 'Highest Value',   month: 'December 2025',value: 'Rs 1.41L',     sub: '8 invoices',       bg: 'bg-emerald-50 dark:bg-emerald-950/40', labelColor: 'text-emerald-600',valueColor: 'text-emerald-700 dark:text-emerald-300' },
  { label: 'Slowest Month',   month: 'February 2025',value: '3 invoices',   sub: 'Rs 63k total',    bg: 'bg-amber-50 dark:bg-amber-950/40',    labelColor: 'text-amber-500',  valueColor: 'text-amber-700 dark:text-amber-300' },
  { label: 'Lowest Value',    month: 'January 2025', value: 'Rs 53k',       sub: '4 invoices',       bg: 'bg-red-50 dark:bg-red-950/40',        labelColor: 'text-red-500',    valueColor: 'text-red-700 dark:text-red-300' },
]

// ── Top by value ──────────────────────────────────────
const topByValue = [
  { name: 'Tariq Javaid',    value: 120000, count: 8,  avg: 15000, pct: 100 },
  { name: 'Pervez Iqbal',    value: 98000,  count: 6,  avg: 16333, pct: 82  },
  { name: 'Hamid Mirza',     value: 85000,  count: 11, avg: 7727,  pct: 71  },
  { name: 'Naveed Siddiqui', value: 74000,  count: 5,  avg: 14800, pct: 62  },
  { name: 'Waqar Younis',    value: 52000,  count: 9,  avg: 5778,  pct: 43  },
]

// ── Top by count ──────────────────────────────────────
const topByCount = [
  { name: 'Hamid Mirza',     count: 11, value: 85000,  avg: 7727,  pct: 100 },
  { name: 'Waqar Younis',    count: 9,  value: 52000,  avg: 5778,  pct: 82  },
  { name: 'Tariq Javaid',    count: 8,  value: 120000, avg: 15000, pct: 73  },
  { name: 'Pervez Iqbal',    count: 6,  value: 98000,  avg: 16333, pct: 55  },
  { name: 'Naveed Siddiqui', count: 5,  value: 74000,  avg: 14800, pct: 45  },
]

// ── Largest invoices ──────────────────────────────────
const largestInvoices = [
  { number: 42, customer: 'Tariq Javaid',    value: 38000, type: 'sale',     date: '12 Dec 2025', items: 6  },
  { number: 38, customer: 'Pervez Iqbal',    value: 34000, type: 'purchase', date: '5 Nov 2025',  items: 4  },
  { number: 45, customer: 'Hamid Mirza',     value: 29000, type: 'sale',     date: '18 Dec 2025', items: 8  },
  { number: 31, customer: 'Naveed Siddiqui', value: 27000, type: 'sale',     date: '22 Oct 2025', items: 5  },
  { number: 29, customer: 'Waqar Younis',    value: 24000, type: 'purchase', date: '9 Oct 2025',  items: 3  },
  { number: 47, customer: 'Rashid Ali',      value: 22000, type: 'sale',     date: '28 Dec 2025', items: 7  },
]

// ── Monthly breakdown ─────────────────────────────────
const invoiceTableData = [
  { period: 'Dec 2025', saleCount: 5, saleValue: 85000, purchaseCount: 3, purchaseValue: 56000, mom: 25  },
  { period: 'Nov 2025', saleCount: 4, saleValue: 68000, purchaseCount: 2, purchaseValue: 44000, mom: -5  },
  { period: 'Oct 2025', saleCount: 6, saleValue: 74000, purchaseCount: 3, purchaseValue: 49000, mom: 21  },
  { period: 'Sep 2025', saleCount: 4, saleValue: 80000, purchaseCount: 2, purchaseValue: 52000, mom: 15  },
  { period: 'Aug 2025', saleCount: 4, saleValue: 58000, purchaseCount: 2, purchaseValue: 38000, mom: -11 },
  { period: 'Jul 2025', saleCount: 4, saleValue: 65000, purchaseCount: 2, purchaseValue: 42000, mom: -10 },
]
</script>

<style scoped>
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
.scrollbar-hide::-webkit-scrollbar { display: none; }
</style>
